import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '@/utils/axios';
import { Plus, User, FileText, Calendar, Building2, Printer, Trash2, Syringe, Pencil, Check, History } from 'lucide-react';
import GlobalAlert from '@/components/GlobalAlert';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import PrescriptionModal from '@/components/PrescriptionModal';
import NewPatientModal from '@/components/NewPatientModal';
import EditPatientModal from '@/components/EditPatientModal';
import EditPrescriptionModal from '@/components/EditPrescriptionModal';
import NewPrescriptionModal from '@/components/NewPrescriptionModal';
import PatientPrescriptionsModal from '@/components/PatientPrescriptionsModal';
import HealthUnitModal from '@/components/HealthUnitModal';
import BatchPrintModal from '@/components/BatchPrintModal';
import QuickPrescriptionModal from '@/components/QuickPrescriptionModal';
import PatientSearchAutocomplete from '@/components/PatientSearchAutocomplete';
import DataImportModal from '@/components/DataImportModal';
import ExamRequestModal from '@/components/ExamRequestModal';
import MedicalDocumentModal from '@/components/MedicalDocumentModal';
import PrescriptionTemplatesManager from '@/components/PrescriptionTemplatesManager';

export default function Dashboard() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [patients, setPatients] = useState([]);
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [patientsWithPrescriptions, setPatientsWithPrescriptions] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUnitId, setSelectedUnitId] = useState('all');
  const [activeTab, setActiveTab] = useState('todos');
  const [expiringPrescriptions, setExpiringPrescriptions] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [prescription, setPrescription] = useState(null);
  const [showPrescriptionModal, setShowPrescriptionModal] = useState(false);
  const [showNewPatientModal, setShowNewPatientModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showNewPrescriptionModal, setShowNewPrescriptionModal] = useState(false);
  const [showHealthUnitModal, setShowHealthUnitModal] = useState(false);
  const [showBatchPrintModal, setShowBatchPrintModal] = useState(false);
  const [showEditPatientModal, setShowEditPatientModal] = useState(false);
  const [showDataImportModal, setShowDataImportModal] = useState(false);
  const [showExamRequestModal, setShowExamRequestModal] = useState(false);
  const [showMedicalDocumentModal, setShowMedicalDocumentModal] = useState(false);
  const [showPrescriptionTemplates, setShowPrescriptionTemplates] = useState(false);
  const [patientToEdit, setPatientToEdit] = useState(null);
  const [selectedPatients, setSelectedPatients] = useState([]);
  const [selectionMode, setSelectionMode] = useState(false);
  const [healthUnits, setHealthUnits] = useState([]);
  const [userHealthUnit, setUserHealthUnit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [totalPrescriptions, setTotalPrescriptions] = useState(0);
  const [expiringUnitFilter, setExpiringUnitFilter] = useState('all');
  const [selectedExpiring, setSelectedExpiring] = useState([]);
  const [expiringSelectionMode, setExpiringSelectionMode] = useState(false);
  const [showQuickPrescriptionModal, setShowQuickPrescriptionModal] = useState(false);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setCurrentUser(user);
    fetchPatients();
    fetchHealthUnits();
    fetchExpiringPrescriptions();
    fetchTotalPrescriptions();
  }, []);

  const fetchTotalPrescriptions = async () => {
    try {
      const response = await axiosInstance.get('/prescriptions/count');
      setTotalPrescriptions(response.data.total || 0);
    } catch (error) {
      console.error('Erro ao buscar total de receitas:', error);
    }
  };

  // Função para normalizar texto (remover acentos)
  const normalizeText = (text) => {
    if (!text) return '';
    return text
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  };

  useEffect(() => {
    const normalizedSearch = normalizeText(searchTerm);
    
    let filtered = patients.filter(patient => {
      const normalizedName = normalizeText(patient.nome);
      return normalizedName.includes(normalizedSearch);
    });
    
    // Filter by unit
    if (selectedUnitId !== 'all') {
      filtered = filtered.filter(patient => patient.unit_id === selectedUnitId);
    }
    
    setFilteredPatients(filtered);
  }, [searchTerm, selectedUnitId, patients]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
    toast.success('Logout realizado com sucesso');
  };

  const fetchPatients = async () => {
    try {
      const response = await axiosInstance.get('/patients');
      // Ordenar pacientes alfabeticamente por nome
      const sortedPatients = response.data.sort((a, b) => 
        a.nome.localeCompare(b.nome, 'pt-BR', { sensitivity: 'base' })
      );
      setPatients(sortedPatients);
      setFilteredPatients(sortedPatients);
      setLoading(false);
      
      // Fetch ALL prescriptions map in batch (todas as receitas de cada paciente)
      try {
        const prescResponse = await axiosInstance.get('/prescriptions/all-by-patient');
        if (prescResponse.data) {
          setPatientsWithPrescriptions(prescResponse.data);
        }
      } catch (error) {
        // Fallback to expiring-map if new endpoint not available
        try {
          const expResponse = await axiosInstance.get('/prescriptions/expiring-map');
          if (expResponse.data) {
            // Convert old format to new format (single prescription as array)
            const converted = {};
            for (const [patientId, presc] of Object.entries(expResponse.data)) {
              converted[patientId] = [{
                id: presc.prescription_id,
                name: "Receita",
                renewal_date: presc.created_at,
                next_renewal: presc.expiry_date,
                days_until_expiry: presc.days_until_expiry
              }];
            }
            setPatientsWithPrescriptions(converted);
          }
        } catch (err) {
          console.log('Mapa de prescrições não disponível');
        }
      }
    } catch (error) {
      console.error('Erro ao buscar pacientes:', error);
      toast.error('Erro ao carregar pacientes');
      setLoading(false);
    }
  };

  const fetchHealthUnits = async () => {
    try {
      const response = await axiosInstance.get('/health-units');
      setHealthUnits(response.data);
      
      // Find user's health unit
      if (currentUser?.unit_id) {
        const userUnit = response.data.find(unit => unit.id === currentUser.unit_id);
        setUserHealthUnit(userUnit);
      }
    } catch (error) {
      console.error('Erro ao buscar unidades:', error);
    }
  };

  const fetchExpiringPrescriptions = async (unitId = 'all') => {
    try {
      const url = unitId && unitId !== 'all' 
        ? `/prescriptions/expiring/soon?days=30&unit_id=${unitId}`
        : '/prescriptions/expiring/soon?days=30';
      const response = await axiosInstance.get(url);
      setExpiringPrescriptions(response.data);
    } catch (error) {
      console.error('Erro ao buscar receitas vencendo:', error);
    }
  };

  const handleExpiringUnitFilterChange = (unitId) => {
    setExpiringUnitFilter(unitId);
    fetchExpiringPrescriptions(unitId);
    // Reset selection when filter changes
    setSelectedExpiring([]);
  };

  // Expiring prescriptions selection functions
  const toggleExpiringSelectionMode = () => {
    setExpiringSelectionMode(!expiringSelectionMode);
    setSelectedExpiring([]);
  };

  const toggleExpiringSelection = (item) => {
    setSelectedExpiring(prev => {
      const isSelected = prev.some(i => i.prescription.id === item.prescription.id);
      if (isSelected) {
        return prev.filter(i => i.prescription.id !== item.prescription.id);
      } else {
        return [...prev, item];
      }
    });
  };

  const selectAllExpiring = () => {
    if (selectedExpiring.length === expiringPrescriptions.length) {
      setSelectedExpiring([]);
    } else {
      setSelectedExpiring([...expiringPrescriptions]);
    }
  };

  const handleExpiringBatchPrint = () => {
    if (selectedExpiring.length === 0) {
      toast.error('Selecione pelo menos uma receita');
      return;
    }
    // Convert selected expiring items to patients format for BatchPrintModal
    const patientsForPrint = selectedExpiring.map(item => ({
      ...item.patient,
      _selectedPrescriptionId: item.prescription.id
    }));
    setSelectedPatients(patientsForPrint);
    setShowBatchPrintModal(true);
  };

  const handleHealthUnitCreated = () => {
    setShowHealthUnitModal(false);
    fetchHealthUnits();
  };

  const toggleSelectionMode = () => {
    setSelectionMode(!selectionMode);
    setSelectedPatients([]);
  };

  const togglePatientSelection = (patient) => {
    setSelectedPatients(prev => {
      const isSelected = prev.some(p => p.id === patient.id);
      if (isSelected) {
        return prev.filter(p => p.id !== patient.id);
      } else {
        return [...prev, patient];
      }
    });
  };

  const handleBatchPrint = () => {
    if (selectedPatients.length === 0) {
      toast.error('Selecione pelo menos um paciente');
      return;
    }
    setShowBatchPrintModal(true);
  };

  const handleBatchDelete = async () => {
    if (selectedPatients.length === 0) {
      toast.error('Selecione pelo menos um paciente');
      return;
    }

    const confirmMessage = `Tem certeza que deseja deletar ${selectedPatients.length} paciente(s)?\n\nEsta ação não pode ser desfeita e irá deletar:\n- Os dados dos pacientes\n- Todas as receitas associadas`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    const deletingToast = toast.loading(`Deletando ${selectedPatients.length} paciente(s)...`);
    
    try {
      let deletedCount = 0;
      let failedCount = 0;
      
      for (const patient of selectedPatients) {
        try {
          await axiosInstance.delete(`/patients/${patient.id}`);
          deletedCount++;
        } catch (error) {
          console.error(`Erro ao deletar paciente ${patient.nome}:`, error);
          failedCount++;
        }
      }

      toast.dismiss(deletingToast);
      
      if (deletedCount > 0) {
        toast.success(`${deletedCount} paciente(s) deletado(s) com sucesso!`);
      }
      
      if (failedCount > 0) {
        toast.error(`Falha ao deletar ${failedCount} paciente(s)`);
      }

      // Reset selection and refresh list
      setSelectedPatients([]);
      setSelectionMode(false);
      fetchPatients();
      
    } catch (error) {
      toast.dismiss(deletingToast);
      console.error('Erro ao deletar pacientes:', error);
      toast.error('Erro ao deletar pacientes');
    }
  };

  const selectAll = () => {
    setSelectedPatients([...filteredPatients]);
  };

  const deselectAll = () => {
    setSelectedPatients([]);
  };

  const handlePatientClick = async (patient) => {
    setSelectedPatient(patient);
    setShowPrescriptionModal(true);
  };

  const handleEdit = (prescription) => {
    setPrescription(prescription);
    setShowPrescriptionModal(false);
    setShowEditModal(true);
  };

  const handleDelete = async () => {
    if (window.confirm(`Deseja realmente deletar o paciente ${selectedPatient.nome}?`)) {
      try {
        await axiosInstance.delete(`/patients/${selectedPatient.id}`);
        toast.success('Paciente deletado com sucesso');
        setShowPrescriptionModal(false);
        setSelectedPatient(null);
        setPrescription(null);
        fetchPatients();
      } catch (error) {
        console.error('Erro ao deletar paciente:', error);
        toast.error('Erro ao deletar paciente');
      }
    }
  };

  const handlePatientCreated = () => {
    setShowNewPatientModal(false);
    fetchPatients();
    fetchTotalPrescriptions();
    toast.success('Paciente cadastrado com sucesso!');
  };

  const handleEditPatient = (patient, e) => {
    if (e) e.stopPropagation();
    setPatientToEdit(patient);
    setShowEditPatientModal(true);
  };

  const handlePatientUpdated = () => {
    setShowEditPatientModal(false);
    setPatientToEdit(null);
    fetchPatients();
  };

  const handlePrescriptionUpdated = async () => {
    setShowEditModal(false);
    const response = await axiosInstance.get(`/prescriptions/patient/${selectedPatient.id}`);
    setPrescription(response.data);
    setShowPrescriptionModal(true);
    fetchExpiringPrescriptions();
    fetchTotalPrescriptions();
    toast.success('Receita atualizada com sucesso!');
  };

  return (
    <div className="min-h-screen bg-[#F4F7F8]">
      <GlobalAlert />
      
      {/* Navbar - Menu Principal */}
      <Navbar
        currentUser={currentUser}
        onLogout={handleLogout}
        onOpenQuickPrescription={() => setShowQuickPrescriptionModal(true)}
        onOpenExamRequest={() => setShowExamRequestModal(true)}
        onOpenDataImport={() => setShowDataImportModal(true)}
        onOpenMedicalDocuments={() => setShowMedicalDocumentModal(true)}
        onOpenPrescriptionTemplates={() => setShowPrescriptionTemplates(true)}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-12 gap-6">
          {/* Header Simplificado */}
          <div className="col-span-12">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-3">
                <h1 className="text-xl font-semibold text-[#1F2933]">Pacientes e Receitas</h1>
                {userHealthUnit && (
                  <Badge className="bg-[#0F766E]/10 text-[#0F766E] border border-[#0F766E]/20 px-2 py-0.5 text-xs font-medium">
                    {userHealthUnit.nome}
                  </Badge>
                )}
              </div>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="col-span-12 flex flex-wrap justify-between items-center gap-3">
            <div className="flex flex-wrap gap-2">
              {selectionMode && (
                <>
                  <Button
                    onClick={selectAll}
                    variant="outline"
                    size="sm"
                    className="rounded-lg px-3 h-9 border-[#D1D5DB] text-[#64748B] hover:text-[#1F2933]"
                    data-testid="select-all-button"
                  >
                    Selecionar Todos
                  </Button>
                  <Button
                    onClick={deselectAll}
                    variant="outline"
                    size="sm"
                    className="rounded-lg px-3 h-9 border-[#D1D5DB] text-[#64748B] hover:text-[#1F2933]"
                    data-testid="deselect-all-button"
                  >
                    Desmarcar Todos
                  </Button>
                  <Button
                    onClick={handleBatchPrint}
                    size="sm"
                    className="bg-[#0F766E] text-white hover:bg-[#0D6660] rounded-lg px-3 h-9"
                    data-testid="batch-print-trigger-button"
                  >
                    <Printer className="w-4 h-4 mr-1" />
                    Imprimir ({selectedPatients.length})
                  </Button>
                  {currentUser?.role === 'administrador' && (
                    <Button
                      onClick={handleBatchDelete}
                      variant="destructive"
                      size="sm"
                      className="bg-[#B91C1C] text-white hover:bg-[#991B1B] rounded-lg px-3 h-9"
                      data-testid="batch-delete-button"
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Deletar ({selectedPatients.length})
                    </Button>
                  )}
                </>
              )}
            </div>
            
            <div className="flex flex-wrap gap-2">
              <Button
                data-testid="toggle-selection-button"
                onClick={toggleSelectionMode}
                variant={selectionMode ? "default" : "outline"}
                size="sm"
                className={`rounded-lg px-3 h-9 ${selectionMode ? 'bg-[#0F766E] text-white' : 'border-[#D1D5DB] text-[#64748B] hover:text-[#1F2933]'}`}
              >
                <div className={`w-4 h-4 mr-2 rounded border ${selectionMode ? 'bg-white border-white' : 'border-[#D1D5DB]'} flex items-center justify-center`}>
                  {selectionMode && <Check className="w-3 h-3 text-[#0F766E]" />}
                </div>
                {selectionMode ? 'Cancelar' : 'Selecionar'}
              </Button>
              <Button
                data-testid="new-unit-button"
                onClick={() => setShowHealthUnitModal(true)}
                size="sm"
                className="bg-white border border-[#D1D5DB] text-[#64748B] hover:text-[#1F2933] hover:border-[#0F766E]/30 rounded-lg px-3 h-9"
              >
                <Building2 className="w-4 h-4 mr-2" />
                Nova Unidade
              </Button>
              <Button
                data-testid="new-patient-button"
                onClick={() => setShowNewPatientModal(true)}
                size="sm"
                className="bg-[#0F766E] text-white hover:bg-[#0D6660] rounded-lg px-3 h-9"
              >
                <Plus className="w-4 h-4 mr-2" />
                Novo Paciente
              </Button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="col-span-12 md:col-span-4">
            <div className="bg-white rounded-xl border border-[#D1D5DB] p-6">
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-lg bg-[#0F766E]/10 flex items-center justify-center">
                  <User className="w-5 h-5 text-[#0F766E]" />
                </div>
                <div>
                  <p className="text-sm text-[#64748B] font-medium">Total de Pacientes</p>
                  <p className="text-2xl font-semibold text-[#1F2933]">{patients.length}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="col-span-12 md:col-span-4">
            <div className="bg-white rounded-xl border border-[#D1D5DB] p-6">
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-lg bg-[#5FB3A2]/10 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-[#5FB3A2]" />
                </div>
                <div>
                  <p className="text-sm text-[#64748B] font-medium">Receitas Ativas</p>
                  <p className="text-2xl font-semibold text-[#1F2933]">{totalPrescriptions}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="col-span-12 md:col-span-4">
            <div className="bg-white rounded-xl border border-[#D1D5DB] p-6">
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-lg bg-[#1E3A8A]/10 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-[#1E3A8A]" />
                </div>
                <div>
                  <p className="text-sm text-[#64748B] font-medium">Data de Hoje</p>
                  <p className="text-lg font-semibold text-[#1F2933]">
                    {new Date().toLocaleDateString('pt-BR')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs and Patient List */}
          <div className="col-span-12">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2 bg-white border border-[#D1D5DB] rounded-lg p-1 mb-6">
                <TabsTrigger value="todos" className="rounded-lg data-[state=active]:bg-[#0F766E] data-[state=active]:text-white" data-testid="tab-todos">
                  Todos os Pacientes
                </TabsTrigger>
                <TabsTrigger value="vencendo" className="rounded-lg relative data-[state=active]:bg-[#0F766E] data-[state=active]:text-white" data-testid="tab-vencendo">
                  Receitas Vencendo
                  {expiringPrescriptions.length > 0 && (
                    <Badge className="ml-2 bg-[#B91C1C] text-white">{expiringPrescriptions.length}</Badge>
                  )}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="todos">
                <div className="bg-white rounded-xl border border-[#D1D5DB] p-6">
                  <div className="mb-6 grid grid-cols-2 gap-4">
                    <PatientSearchAutocomplete
                      value={searchTerm}
                      onChange={setSearchTerm}
                      onSelect={(patient) => handlePatientClick(patient)}
                      placeholder="Buscar paciente por nome..."
                      healthUnits={healthUnits}
                    />
                    <div>
                      <Select value={selectedUnitId} onValueChange={setSelectedUnitId}>
                        <SelectTrigger 
                          className="bg-[#F4F7F8] border-[#D1D5DB] focus:border-[#0F766E] focus:bg-white transition-all rounded-lg h-11"
                          data-testid="unit-filter-select"
                        >
                          <SelectValue placeholder="Filtrar por unidade" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todas as Unidades</SelectItem>
                          {healthUnits.map((unit) => (
                            <SelectItem key={unit.id} value={unit.id}>
                              {unit.nome}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-3 max-h-[600px] overflow-y-auto">
                    {loading ? (
                      <p className="text-center text-muted-foreground py-8">Carregando pacientes...</p>
                    ) : filteredPatients.length === 0 ? (
                      <p className="text-center text-muted-foreground py-8">Nenhum paciente encontrado</p>
                    ) : (
                      filteredPatients.map((patient) => (
                        <div
                          key={patient.id}
                          data-testid={`patient-card-${patient.id}`}
                          onClick={(e) => {
                            if (selectionMode) {
                              e.stopPropagation();
                              togglePatientSelection(patient);
                            } else {
                              handlePatientClick(patient);
                            }
                          }}
                          className={`group relative bg-white rounded-xl border-l-4 ${
                            selectionMode && selectedPatients.some(p => p.id === patient.id)
                              ? 'border-l-accent bg-accent/5'
                              : 'border-l-primary'
                          } border-y border-r border-gray-100 p-4 hover:translate-x-1 transition-transform cursor-pointer hover:shadow-md`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              {selectionMode && (
                                <Checkbox
                                  checked={selectedPatients.some(p => p.id === patient.id)}
                                  onCheckedChange={() => togglePatientSelection(patient)}
                                  onClick={(e) => e.stopPropagation()}
                                  data-testid={`patient-checkbox-${patient.id}`}
                                />
                              )}
                              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white font-bold text-lg">
                                {patient.nome.charAt(0)}
                              </div>
                              <div className="flex-1">
                                <h3 className="font-bold text-foreground text-lg">{patient.nome}</h3>
                                <p className="text-sm text-muted-foreground font-jetbrains">
                                  Nascimento: {patient.data_nascimento}
                                </p>
                                {/* Mostrar TODAS as receitas do paciente */}
                                {patientsWithPrescriptions[patient.id] && Array.isArray(patientsWithPrescriptions[patient.id]) && patientsWithPrescriptions[patient.id].length > 0 && (
                                  <div className="mt-2 space-y-2">
                                    {patientsWithPrescriptions[patient.id].map((presc, idx) => {
                                      const isExpiringSoon = presc.days_until_expiry !== null && presc.days_until_expiry <= 30;
                                      const isExpired = presc.days_until_expiry !== null && presc.days_until_expiry <= 0;
                                      return (
                                        <div 
                                          key={presc.id || idx} 
                                          className={`text-xs p-2 rounded-lg ${
                                            isExpired ? 'bg-red-50 border border-red-200' :
                                            isExpiringSoon ? 'bg-yellow-50 border border-yellow-200' :
                                            'bg-gray-50 border border-gray-200'
                                          }`}
                                        >
                                          <div className="font-medium text-gray-700 mb-1">
                                            📋 {presc.name || 'Receita'} 
                                            {presc.medications_count > 0 && (
                                              <span className="text-gray-500 ml-1">({presc.medications_count} med.)</span>
                                            )}
                                          </div>
                                          <div className="flex flex-wrap gap-x-4 gap-y-1">
                                            <span className="text-gray-600">
                                              🟢 Renovada: {presc.renewal_date ? new Date(presc.renewal_date).toLocaleDateString('pt-BR') : 'N/A'}
                                            </span>
                                            <span className={`${isExpired ? 'text-red-600 font-semibold' : isExpiringSoon ? 'text-yellow-700' : 'text-gray-600'}`}>
                                              📅 Próx. renovação: {presc.next_renewal ? new Date(presc.next_renewal).toLocaleDateString('pt-BR') : 'N/A'}
                                              {isExpired && ' (VENCIDA)'}
                                              {!isExpired && isExpiringSoon && ` (${presc.days_until_expiry}d)`}
                                            </span>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {/* Badge de alerta para receita mais urgente */}
                              {patientsWithPrescriptions[patient.id] && Array.isArray(patientsWithPrescriptions[patient.id]) && 
                               patientsWithPrescriptions[patient.id].some(p => p.days_until_expiry !== null && p.days_until_expiry <= 10) && (
                                <Badge className={`${
                                  patientsWithPrescriptions[patient.id].some(p => p.days_until_expiry !== null && p.days_until_expiry <= 0)
                                    ? 'bg-red-600' 
                                    : patientsWithPrescriptions[patient.id].some(p => p.days_until_expiry !== null && p.days_until_expiry <= 5)
                                    ? 'bg-orange-500'
                                    : 'bg-yellow-500'
                                }`}>
                                  ⚠️ {patientsWithPrescriptions[patient.id].some(p => p.days_until_expiry !== null && p.days_until_expiry <= 0)
                                    ? 'VENCIDA' 
                                    : `${Math.min(...patientsWithPrescriptions[patient.id].filter(p => p.days_until_expiry !== null).map(p => p.days_until_expiry))} dias`}
                                </Badge>
                              )}
                              <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => handleEditPatient(patient, e)}
                                  className="h-8 w-8 p-0 rounded-full hover:bg-blue-100"
                                  title="Editar Paciente"
                                  data-testid={`edit-patient-${patient.id}`}
                                >
                                  <Pencil className="w-4 h-4 text-blue-600" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/paciente/${patient.id}/historico`);
                                  }}
                                  className="h-8 w-8 p-0 rounded-full hover:bg-purple-100"
                                  title="Ver Histórico Completo"
                                  data-testid={`history-patient-${patient.id}`}
                                >
                                  <History className="w-4 h-4 text-purple-600" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/vaccination-card/${patient.id}`);
                                  }}
                                  className="h-8 w-8 p-0 rounded-full hover:bg-green-100"
                                  title="Cartão Vacinal"
                                >
                                  <Syringe className="w-5 h-5 text-green-600" />
                                </Button>
                                <FileText className="w-6 h-6 text-primary" />
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="vencendo">
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                  <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                    <h3 className="text-xl font-bold">Receitas Próximas do Vencimento (30 dias)</h3>
                    <div className="flex items-center gap-3">
                      {/* Selection mode toggle */}
                      <Button
                        variant={expiringSelectionMode ? "default" : "outline"}
                        size="sm"
                        onClick={toggleExpiringSelectionMode}
                        className="rounded-lg"
                      >
                        {expiringSelectionMode ? 'Cancelar Seleção' : 'Selecionar Múltiplos'}
                      </Button>
                      
                      {/* Unit filter */}
                      <Select value={expiringUnitFilter} onValueChange={handleExpiringUnitFilterChange}>
                        <SelectTrigger className="w-[250px] bg-gray-50 border-transparent focus:border-primary/30 focus:bg-white transition-all rounded-lg h-10">
                          <Building2 className="w-4 h-4 mr-2 text-muted-foreground" />
                          <SelectValue placeholder="Filtrar por unidade" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todas as Unidades</SelectItem>
                          {healthUnits.map((unit) => (
                            <SelectItem key={unit.id} value={unit.id}>
                              {unit.nome}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  {/* Selection actions bar */}
                  {expiringSelectionMode && (
                    <div className="flex items-center justify-between mb-4 p-3 bg-primary/5 rounded-lg border border-primary/20">
                      <div className="flex items-center gap-3">
                        <Checkbox
                          checked={selectedExpiring.length === expiringPrescriptions.length && expiringPrescriptions.length > 0}
                          onCheckedChange={selectAllExpiring}
                          id="select-all-expiring"
                        />
                        <label htmlFor="select-all-expiring" className="text-sm font-medium cursor-pointer">
                          {selectedExpiring.length === expiringPrescriptions.length 
                            ? 'Desmarcar Todos' 
                            : 'Selecionar Todos'}
                        </label>
                        {selectedExpiring.length > 0 && (
                          <Badge variant="secondary" className="ml-2">
                            {selectedExpiring.length} selecionado(s)
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          onClick={handleExpiringBatchPrint}
                          disabled={selectedExpiring.length === 0}
                          className="bg-primary hover:bg-primary/90 rounded-lg"
                        >
                          <Printer className="w-4 h-4 mr-2" />
                          Imprimir Selecionados ({selectedExpiring.length})
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  <div className="space-y-3 max-h-[600px] overflow-y-auto">
                    {expiringPrescriptions.length === 0 ? (
                      <p className="text-center text-muted-foreground py-8">
                        {expiringUnitFilter !== 'all' 
                          ? 'Nenhuma receita próxima do vencimento nesta unidade'
                          : 'Nenhuma receita próxima do vencimento'}
                      </p>
                    ) : (
                      expiringPrescriptions.map((item) => {
                        const isSelected = selectedExpiring.some(i => i.prescription.id === item.prescription.id);
                        return (
                          <div
                            key={item.prescription.id}
                            data-testid={`expiring-card-${item.prescription.id}`}
                            onClick={() => {
                              if (expiringSelectionMode) {
                                toggleExpiringSelection(item);
                              } else {
                                handlePatientClick(item.patient);
                              }
                            }}
                            className={`group relative bg-white rounded-xl border-l-4 ${
                              isSelected 
                                ? 'border-l-primary bg-primary/5 border-primary/30' 
                                : 'border-l-red-500 border-gray-100'
                            } border-y border-r p-4 hover:translate-x-1 transition-all cursor-pointer hover:shadow-md`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                {expiringSelectionMode && (
                                  <Checkbox
                                    checked={isSelected}
                                    onCheckedChange={() => toggleExpiringSelection(item)}
                                    onClick={(e) => e.stopPropagation()}
                                    className="data-[state=checked]:bg-primary"
                                  />
                                )}
                                <div className={`w-12 h-12 rounded-full ${
                                  isSelected 
                                    ? 'bg-gradient-to-br from-primary to-primary/70' 
                                    : 'bg-gradient-to-br from-red-500 to-orange-500'
                                } flex items-center justify-center text-white font-bold text-lg`}>
                                  {item.patient.nome.charAt(0)}
                                </div>
                                <div>
                                  <h3 className="font-bold text-foreground text-lg">{item.patient.nome}</h3>
                                  <p className="text-sm text-muted-foreground font-jetbrains">
                                    {item.days_until_expiry > 0 
                                      ? `Vence em ${item.days_until_expiry} dia(s)` 
                                      : 'VENCIDA'}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    Validade: {new Date(item.prescription.data_validade).toLocaleDateString('pt-BR')}
                                  </p>
                                </div>
                              </div>
                              <Badge className={item.days_until_expiry <= 0 ? 'bg-red-600' : 'bg-orange-500'}>
                                {item.days_until_expiry <= 0 ? 'Vencida' : 'Vencendo'}
                              </Badge>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>

      {showPrescriptionModal && selectedPatient && (
        <PatientPrescriptionsModal
          open={showPrescriptionModal}
          onClose={() => {
            setShowPrescriptionModal(false);
            setSelectedPatient(null);
          }}
          patient={selectedPatient}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onNewPrescription={() => {
            setShowPrescriptionModal(false);
            setShowNewPrescriptionModal(true);
          }}
          onRefresh={fetchPatients}
        />
      )}

      {showNewPrescriptionModal && selectedPatient && (
        <NewPrescriptionModal
          open={showNewPrescriptionModal}
          onClose={() => setShowNewPrescriptionModal(false)}
          patient={selectedPatient}
          onSuccess={() => {
            setShowNewPrescriptionModal(false);
            setShowPrescriptionModal(true);
            fetchPatients();
            fetchTotalPrescriptions();
          }}
        />
      )}

      {showNewPatientModal && (
        <NewPatientModal
          open={showNewPatientModal}
          onClose={() => setShowNewPatientModal(false)}
          onSuccess={handlePatientCreated}
          healthUnits={healthUnits}
        />
      )}

      {showHealthUnitModal && (
        <HealthUnitModal
          open={showHealthUnitModal}
          onClose={() => setShowHealthUnitModal(false)}
          onSuccess={handleHealthUnitCreated}
        />
      )}

      {showBatchPrintModal && (
        <BatchPrintModal
          open={showBatchPrintModal}
          onClose={() => setShowBatchPrintModal(false)}
          selectedPatients={selectedPatients}
        />
      )}

      {showEditModal && selectedPatient && prescription && (
        <EditPrescriptionModal
          open={showEditModal}
          onClose={() => setShowEditModal(false)}
          patient={selectedPatient}
          prescription={prescription}
          onSuccess={handlePrescriptionUpdated}
        />
      )}

      {showEditPatientModal && patientToEdit && (
        <EditPatientModal
          open={showEditPatientModal}
          onClose={() => {
            setShowEditPatientModal(false);
            setPatientToEdit(null);
          }}
          patient={patientToEdit}
          onSuccess={handlePatientUpdated}
          healthUnits={healthUnits}
        />
      )}

      {showQuickPrescriptionModal && (
        <QuickPrescriptionModal
          open={showQuickPrescriptionModal}
          onClose={() => setShowQuickPrescriptionModal(false)}
        />
      )}

      {/* Data Import Modal - Admin Only */}
      {showDataImportModal && (
        <DataImportModal
          open={showDataImportModal}
          onClose={() => setShowDataImportModal(false)}
          onSuccess={() => {
            fetchPatients();
            fetchExpiringPrescriptions();
          }}
        />
      )}

      {/* Modal de Pedido de Exames */}
      <ExamRequestModal
        open={showExamRequestModal}
        onClose={() => setShowExamRequestModal(false)}
      />

      {/* Modal de Documentos Médicos */}
      <MedicalDocumentModal
        open={showMedicalDocumentModal}
        onClose={() => setShowMedicalDocumentModal(false)}
        patient={selectedPatient}
      />

      {/* Modal de Gerenciamento de Templates de Receitas */}
      <PrescriptionTemplatesManager
        open={showPrescriptionTemplates}
        onClose={() => setShowPrescriptionTemplates(false)}
      />
    </div>
  );
}